import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useParams } from 'react-router-dom';
import { BarChart3, FileText, Home, FolderOpen, Building2, User } from 'lucide-react';
import { Dashboard } from './components/Dashboard';
import { DealDashboard } from './components/DealDashboard';
import { LOIPage } from './components/LOIPage';
import { LOIPage1 } from './components/LOIPage1';
import { LOIPage2 } from './components/LOIPage2';
import { DealLOIManager } from './components/DealLOIManager';
import { DealLOIPage1 } from './components/DealLOIPage1';
import { DealLOIPage2 } from './components/DealLOIPage2';
import { NDAPage } from './components/NDAPage';
import { LenderSellerAnalysis } from './components/LenderSellerAnalysis';
import { AdminPanel } from './components/AdminPanel';
import { DocumentManager } from './components/DocumentManager';
import { DocumentManagementSplitView } from './components/DocumentManagementSplitView';
import { DealDocumentManager } from './components/DealDocumentManager';
import { DealManager } from './components/DealManager';
import { BuyerFinancialAnalysis } from './components/BuyerFinancialAnalysis';
import { BuyerFinancialAnalysisSimple } from './components/BuyerFinancialAnalysisSimple';
import { BuyerFinancialAnalysisTest } from './components/BuyerFinancialAnalysisTest';
import { BuyerFinancialAnalysisMinimal } from './components/BuyerFinancialAnalysisMinimal';
import { BuyerFinancialAnalysisDebug } from './components/BuyerFinancialAnalysisDebug';
import { BuyerAnalysisManager } from './components/BuyerAnalysisManager';
import { DealBuyerAnalysisManager } from './components/DealBuyerAnalysisManager';
import { OnboardingManager, useOnboardingStatus } from './components/OnboardingManager';
import { ErrorBoundary } from './components/ErrorBoundary';
import { DealProvider, useDeal } from './contexts/DealContext';
import { DealSelector } from './components/DealSelector';
import { DealRouteGuard } from './components/DealRouteGuard';
import ProformaPage from './components/ProformaPage';
import { ScenarioAnalysisPage } from './components/ScenarioAnalysisPage';
import ArchivedDealsPageWrapper from './pages/ArchivedDealsPage';
import { SignedIn, SignedOut, SignInButton, SignUpButton, UserButton, useUser, useAuth, SignIn, SignUp } from '@clerk/clerk-react';
import { useClerkIntegration } from './hooks/useClerkIntegration';
import { useUserRole } from './hooks/useUserRole';
import { useLOIDocuments } from './hooks/useLOIDocuments';

// Wrapper component for deal-specific documents
function DealDocumentsWrapper() {
  const { dealId } = useParams<{ dealId: string }>();
  return <DocumentManagementSplitView dealId={dealId} />;
}

// Enhanced role management with database integration
const getUserRole = (user: any, dbRole: string | null): string => {
  if (!user) return 'None';
  
  // Use database role if available, otherwise fall back to email-based logic
  if (dbRole) {
    return dbRole;
  }
  
  // Fallback: Check if user email matches test@leestauss.com for Admin role
  const email = user.emailAddresses?.[0]?.emailAddress;
  if (email === 'test@leestauss.com') {
    return 'Admin';
  }
  
  // Default to 'Lender' for other users
  return 'Lender';
};

// Navigation Header Component
function NavigationHeader() {
  const { user } = useUser();
  const location = useLocation();
  const { userRole, isLoading: roleLoading } = useUserRole();
  const { isSynced, isLoading: syncLoading } = useClerkIntegration();
  const { selectedDeal, availableDeals } = useDeal();
  const { hasLOIDocuments, hasOption1, hasOption2, isLoading: loiLoading } = useLOIDocuments(selectedDeal?.deal_id || null);
  
  // Handle case where selectedDeal might be null after deletion
  const safeSelectedDeal = selectedDeal || null;
  
  const isDashboard = location.pathname === '/' || location.pathname.startsWith('/deals/') && location.pathname.endsWith('/dashboard');
  const isLOI = location.pathname.startsWith('/deals/') && (location.pathname.includes('/loi') || location.pathname.includes('/loi1') || location.pathname.includes('/loi2'));
  const isDocuments = location.pathname === '/documents' || (location.pathname.startsWith('/deals/') && location.pathname.includes('/documents'));
  const isDeals = location.pathname === '/deals' || location.pathname === '/deals/create';
  const isBuyerAnalysis = location.pathname.startsWith('/deals/') && location.pathname.includes('/buyer-analysis');
  const isProforma = location.pathname.startsWith('/deals/') && location.pathname.includes('/proforma');

  const getPageTitle = () => {
    if (location.pathname === '/deals/create') return 'Create New Deal';
    if (location.pathname === '/deals') return 'Deal Management';
    if (location.pathname === '/documents') return 'Document Management';
    if (location.pathname.includes('/loi1')) return 'Option 1';
    if (location.pathname.includes('/loi2')) return 'Option 1a';
    if (location.pathname.includes('/loi')) return 'Letter of Intent';
    if (location.pathname.includes('/documents')) return 'Document Management';
    if (location.pathname.includes('/buyer-analysis')) return 'Buyer Financial Analysis';
    if (location.pathname.includes('/proforma')) return 'Proforma';
    if (location.pathname.includes('/dashboard')) return 'Deal Dashboard';
    return 'Financial Dashboard';
  };

  const getPageSubtitle = () => {
    if (location.pathname === '/deals/create') return 'Upload documents and configure deal details';
    if (location.pathname === '/deals') return 'Create and manage business acquisition deals';
    if (location.pathname.includes('/loi1')) return '$1.1M Deal Structure';
    if (location.pathname.includes('/loi2')) return '$1.1M Interest-Only Structure (5yr balloon)';
    if (location.pathname.includes('/loi')) return 'Business Acquisition Document';
    if (location.pathname.includes('/documents')) return 'Upload, manage, and share financial documents';
    if (location.pathname.includes('/buyer-analysis')) return 'SBA Personal Financial Statement, Tax Returns, Bank Statements, Credit Reports';
    if (location.pathname.includes('/proforma')) return 'Single-page proforma: narrative + projections + timeline';
    if (location.pathname.includes('/dashboard') && safeSelectedDeal) return `${safeSelectedDeal.business_name} • Financial Analysis`;
    return 'January 2020 - May 2025 • 65 Months of Data';
  };

  return (
    <div className="bg-white shadow-sm border-b border-gray-100">
      <div className="max-w-none mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-4 sm:py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Deal Analyzer
              </h1>
            </div>
          </div>
          
          {/* Deal Selector */}
          <div className="mb-4 sm:mb-0">
            <DealSelector />
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {safeSelectedDeal && (
              <Link
                to={`/deals/${safeSelectedDeal.deal_id}/dashboard`}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                  isDashboard
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Home className="w-4 h-4" />
                Dashboard
              </Link>
            )}
            {(() => {
              const role = getUserRole(user, userRole);
              // Show Documents for users with deals OR for Admin/Buyer (even without deals)
              if (role === 'Admin' || role === 'Buyer' || availableDeals.length > 0) {
                // Deal-specific Documents for Admin/Buyer when a deal is selected
                if ((role === 'Admin' || role === 'Buyer') && safeSelectedDeal) {
                  return (
                    <Link
                      to={`/deals/${safeSelectedDeal.deal_id}/documents`}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                        isDocuments
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <FolderOpen className="w-4 h-4" />
                      Documents
                    </Link>
                  );
                }
                // Global Documents page for Admin and Buyer (when no deal selected)
                if ((role === 'Admin' || role === 'Buyer') && !safeSelectedDeal) {
                  return (
                    <Link
                      to="/documents"
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                        isDocuments
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <FolderOpen className="w-4 h-4" />
                      Documents
                    </Link>
                  );
                }
                // Deal-specific Documents for other roles when a deal is selected
                if ((role === 'Lender' || role === 'Seller' || role === 'Advisor' || role === 'Attorney') && safeSelectedDeal) {
                  // Check if user has signed NDA for this deal (has read access)
                  const hasSignedNda = safeSelectedDeal.access_type === 'read';
                  const hasWriteAccess = safeSelectedDeal.access_type !== 'read';
                  
                  // Show Documents if user has write access OR has signed NDA (read access)
                  if (hasWriteAccess || hasSignedNda) {
                    return (
                      <Link
                        to={`/deals/${safeSelectedDeal.deal_id}/documents`}
                        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                          isDocuments
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <FolderOpen className="w-4 h-4" />
                        Documents
                      </Link>
                    );
                  }
                }
              }
              return null;
            })()}
            {(() => {
              const role = getUserRole(user, userRole);
              // Show Manage Deals if user has deals (regardless of role or access type)
              if (availableDeals.length > 0) {
                return (
                  <Link
                    to="/deals"
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                      isDeals
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Building2 className="w-4 h-4" />
                    Manage Deals
                  </Link>
                );
              }
              return null;
            })()}
            {(() => {
              const role = getUserRole(user, userRole);
              // Only show deal-specific navigation if user has deals and has write/admin access
              // Buyer Analysis should NEVER be accessible to read-only users (even those who signed NDAs)
              if ((role === 'Admin' || role === 'Buyer' || role === 'Lender' || role === 'Advisor') && safeSelectedDeal && availableDeals.length > 0 && safeSelectedDeal.access_type !== 'read') {
                return (
                  <>
                    <Link
                      to={`/deals/${selectedDeal.deal_id}/buyer-analysis`}
                      className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                        isBuyerAnalysis
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <User className="w-4 h-4" />
                      Buyer Analysis
                    </Link>
                    {/* Proforma and LOI are accessed under ScenarioManager within deal views */}
                  </>
                );
              }
              return null;
            })()}
            {/* Admin Panel Link - Always show for Admin users */}
            {(() => {
              const role = getUserRole(user, userRole);
              if (role === 'Admin') {
                return (
                  <Link
                    to="/admin"
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-colors min-h-[44px] ${
                      location.pathname === '/admin'
                        ? 'bg-red-600 text-white'
                        : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                    }`}
                  >
                    Admin Panel
                  </Link>
                );
              }
              return null;
            })()}
            {selectedDeal && availableDeals.length > 0 && (
              <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                {(() => {
                  const role = getUserRole(user, userRole);
                  // Only show LOI options if user has permission, LOI documents exist, and user has write/admin access
                  if ((role === 'Admin' || role === 'Buyer' || role === 'Lender' || role === 'Seller' || role === 'Advisor' || role === 'Attorney') && selectedDeal.access_type !== 'read') {
                    if (hasLOIDocuments) {
                      return (
                        <>
                          {/* Show general LOI link for standard LOI documents */}
                          <Link
                            to={`/deals/${selectedDeal.deal_id}/loi`}
                            className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm min-h-[44px] ${
                              location.pathname.includes('/loi') && !location.pathname.includes('/loi1') && !location.pathname.includes('/loi2')
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            LOI
                          </Link>
                          {hasOption1 && (
                            <Link
                              to={`/deals/${selectedDeal.deal_id}/loi1`}
                              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm min-h-[44px] ${
                                location.pathname.includes('/loi1')
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              Option 1
                            </Link>
                          )}
                          {hasOption2 && (
                            <Link
                              to={`/deals/${selectedDeal.deal_id}/loi2`}
                              className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm min-h-[44px] ${
                                location.pathname.includes('/loi2')
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              Option 1a
                            </Link>
                          )}
                        </>
                      );
                    } else {
                      // Show LOI button for all users (read-only users can view, write users can create)
                      return (
                        <Link
                          to={`/deals/${selectedDeal.deal_id}/loi`}
                          className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm min-h-[44px] ${
                            location.pathname.includes('/loi')
                              ? 'bg-blue-600 text-white'
                              : selectedDeal.access_type === 'read' 
                                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                        >
                          {selectedDeal.access_type === 'read' ? 'View LOI' : 'Create LOI'}
                        </Link>
                      );
                    }
                  }
                  return null;
                })()}
                {(() => {
                  const role = getUserRole(user, userRole);
                  // Show Analysis button for all users (read-only users can view, write users can edit)
                  if ((role === 'Lender' || role === 'Seller' || role === 'Advisor') && selectedDeal) {
                    return (
                      <Link
                        to={`/deals/${selectedDeal.deal_id}/analysis`}
                        className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm min-h-[44px] ${
                          location.pathname.includes('/analysis')
                            ? 'bg-blue-600 text-white'
                            : selectedDeal.access_type === 'read'
                              ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        Analysis
                      </Link>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            <div className="flex items-center gap-2 ml-auto">
              <SignedOut>
                <SignInButton />
                <SignUpButton />
              </SignedOut>
              <SignedIn>
                <UserButton />
              </SignedIn>
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
}

function App() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { actor } = useAuth(); // Check for impersonation
  const { userRole } = useUserRole();
  const { needsOnboarding, isLoading: onboardingLoading } = useOnboardingStatus();
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  // Note: Removed automatic NDA redirect - now showing NDA deals in deal list instead
  
  const role = getUserRole(user, userRole);

  // Show loading state while Clerk is loading
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Initializing...</p>
        </div>
      </div>
    );
  }

  // Check onboarding BEFORE rendering routes (only for signed-in users from root/deals pages)
  if (isSignedIn && !onboardingComplete) {
    const path = window.location.pathname;
    const shouldCheckOnboarding = path === '/' || path === '/deals' || path === '/deals/create';
    
    if (shouldCheckOnboarding && (onboardingLoading || needsOnboarding === null)) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Checking your account...</p>
          </div>
        </div>
      );
    }

    if (shouldCheckOnboarding && needsOnboarding === true) {
      return <OnboardingManager onComplete={() => setOnboardingComplete(true)} />;
    }
  }

  // Render the main app
  return (
    <Router>
      <DealProvider>
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
          {/* Impersonation indicator */}
          {actor && (
            <div className="bg-orange-600 text-white px-4 py-2 text-center font-semibold text-sm">
              🎭 IMPERSONATION ACTIVE - Viewing as: {user?.primaryEmailAddress?.emailAddress}
            </div>
          )}
          {isSignedIn && <NavigationHeader />}
          
          <div className="px-4 sm:px-6 lg:px-8 xl:px-12">
            <div className="max-w-none mx-auto">
              <ErrorBoundary>
                <Routes>
                  {isSignedIn ? (
                    <>
                      {/* Main landing page - redirect to deals */}
                      <Route path="/" element={<Navigate to="/deals" replace />} />
                      
                      {/* Deal management routes */}
                      <Route path="/deals" element={<ErrorBoundary><DealManager /></ErrorBoundary>} />
                      <Route path="/deals/create" element={<ErrorBoundary><DealManager /></ErrorBoundary>} />
                      <Route path="/deals/archived" element={<ErrorBoundary><ArchivedDealsPageWrapper /></ErrorBoundary>} />
                      
                      {/* Global documents route */}
                      <Route path="/documents" element={<ErrorBoundary><DocumentManagementSplitView /></ErrorBoundary>} />
                      
                      {/* Deal-specific routes */}
                      <Route path="/deals/:dealId/dashboard" element={<ErrorBoundary><DealRouteGuard><DealDashboard /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/loi" element={<ErrorBoundary><DealRouteGuard><DealLOIManager /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/scenarios/:scenarioId/loi" element={<ErrorBoundary><DealRouteGuard><DealLOIManager /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/loi1" element={<ErrorBoundary><DealRouteGuard><DealLOIPage1 /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/loi2" element={<ErrorBoundary><DealRouteGuard><DealLOIPage2 /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/buyer-analysis" element={<ErrorBoundary><DealRouteGuard><DealBuyerAnalysisManager /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/proforma" element={<ErrorBoundary><DealRouteGuard><ProformaPage /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/scenarios/:scenarioId/proforma" element={<ErrorBoundary><DealRouteGuard><ProformaPage /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/scenarios/:scenarioId/analysis" element={<ErrorBoundary><DealRouteGuard><ScenarioAnalysisPage /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/documents" element={<ErrorBoundary><DealRouteGuard><DealDocumentsWrapper /></DealRouteGuard></ErrorBoundary>} />
                      <Route path="/deals/:dealId/analysis" element={<ErrorBoundary><DealRouteGuard><LenderSellerAnalysis /></DealRouteGuard></ErrorBoundary>} />
                      
                      {/* NDA route - accessible without authentication for recipients */}
                      <Route path="/nda" element={<ErrorBoundary><NDAPage /></ErrorBoundary>} />
                      
                      {/* Admin routes */}
                      {role === 'Admin' && (
                        <Route path="/admin" element={<ErrorBoundary><AdminPanel /></ErrorBoundary>} />
                      )}
                      
                      {/* Legacy routes for backward compatibility - redirect to deals */}
                      <Route path="/loi" element={<Navigate to="/deals" replace />} />
                      <Route path="/loi1" element={<Navigate to="/deals" replace />} />
                      <Route path="/loi2" element={<Navigate to="/deals" replace />} />
                      <Route path="/analysis" element={<Navigate to="/deals" replace />} />
                      {/* keep legacy redirects, except documents now has real route */}
                      <Route path="/buyer-analysis" element={<Navigate to="/deals" replace />} />
                    </>
                  ) : (
                    // Show Clerk's authentication forms for non-authenticated users
                    <>
                      <Route path="/sign-up" element={
                        <div className="min-h-screen flex items-center justify-center">
                          <SignUp />
                        </div>
                      } />
                      <Route path="/sign-in" element={
                        <div className="min-h-screen flex items-center justify-center">
                          <SignIn />
                        </div>
                      } />
                      <Route path="*" element={
                        <div className="min-h-screen flex items-center justify-center">
                          <SignIn />
                        </div>
                      } />
                    </>
                  )}
                </Routes>
              </ErrorBoundary>
            </div>
          </div>
        </div>
      </DealProvider>
    </Router>
  );
}

export default App;